import { useState, useEffect } from 'react'
import { Button } from './Button'
import { Alert } from './Alert'
import { Modal } from './Modal'
import {
  useIdentityCheckService,
  IdentityCheck,
  IdentityCheckPointsSummary,
  DiditReport,
} from '../services/identityCheckService'
import { useDocumentService } from '../services/documentService'
import { identityCheckStatusClasses } from '../statusStyles'
import { IDENTITY_DOC_LABELS as DOC_TYPE_LABELS, IDENTITY_DOC_TIERS } from '../identityCheckPoints'

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const REPORT_FIELDS: { key: keyof DiditReport; label: string }[] = [
  { key: 'fullName', label: 'Full name' },
  { key: 'dateOfBirth', label: 'Date of birth' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'documentType', label: 'Document type' },
  { key: 'documentNumber', label: 'Document number' },
  { key: 'issuingStateName', label: 'Issuing country' },
  { key: 'dateOfIssue', label: 'Date of issue' },
  { key: 'expirationDate', label: 'Expiry date' },
  { key: 'address', label: 'Address' },
]

interface IdentityVerificationPanelProps {
  matterId: string
  clientId: string
  personLabel: string
}

// One person's KYC verification on one matter, reviewed by the tenant —
// starting a Didit session or uploading an ID document both now happen
// client-side only, via the dedicated client portal (see PortalPage.tsx),
// so this panel is read/review only: the list of checks the client has
// submitted, status, and a live full Didit report (name, DOB, document
// details, photos) on demand. Embedded once per client on a matter,
// mirroring how SignatureRequests is embedded in MatterDetail.
export const IdentityVerificationPanel: React.FC<IdentityVerificationPanelProps> = ({ matterId, clientId, personLabel }) => {
  const [identityChecks, setIdentityChecks] = useState<IdentityCheck[]>([])
  const [pointsSummary, setPointsSummary] = useState<IdentityCheckPointsSummary | null>(null)
  const [checksLoading, setChecksLoading] = useState(false)
  const [checksError, setChecksError] = useState<string | null>(null)
  const [reviewingId, setReviewingId] = useState<string | null>(null)

  const [reportModalCheck, setReportModalCheck] = useState<IdentityCheck | null>(null)
  const [report, setReport] = useState<DiditReport | null>(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)

  const [medicareModalCheck, setMedicareModalCheck] = useState<IdentityCheck | null>(null)

  const { listIdentityChecks, updateIdentityCheck, getIdentityCheckReport } = useIdentityCheckService()
  const { getDownloadUrl } = useDocumentService()

  const loadIdentityChecks = async () => {
    setChecksLoading(true)
    setChecksError(null)
    try {
      const { identityChecks: results, pointsSummary: summary } = await listIdentityChecks(matterId, clientId)
      setIdentityChecks(results)
      setPointsSummary(summary)
    } catch (err) {
      setChecksError(err instanceof Error ? err.message : 'Failed to load identity checks')
    } finally {
      setChecksLoading(false)
    }
  }

  useEffect(() => {
    loadIdentityChecks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, clientId])

  const handleViewCheckDocument = async (check: IdentityCheck) => {
    if (!check.documentId) return
    setChecksError(null)
    try {
      const url = await getDownloadUrl(matterId, check.documentId)
      window.open(url, '_blank')
    } catch (err) {
      setChecksError(err instanceof Error ? err.message : 'Failed to open document')
    }
  }

  const handleReviewCheck = async (check: IdentityCheck, status: 'verified' | 'rejected') => {
    setChecksError(null)
    setReviewingId(check.id)
    try {
      await updateIdentityCheck(matterId, check.id, { status })
      // Reload rather than patch the row in place — the points summary
      // (and the newly-verified row's own points) are only ever computed
      // server-side (see identity-check-points.ts), so a full reload is the
      // only way to keep both in sync with this change.
      await loadIdentityChecks()
    } catch (err) {
      setChecksError(err instanceof Error ? err.message : 'Failed to update identity check')
    } finally {
      setReviewingId(null)
    }
  }

  const handleViewReport = async (check: IdentityCheck) => {
    setReportModalCheck(check)
    setReport(null)
    setReportError(null)
    setReportLoading(true)
    try {
      const result = await getIdentityCheckReport(matterId, check.id)
      setReport(result)
    } catch (err) {
      setReportError(err instanceof Error ? err.message : 'Failed to load verification report')
    } finally {
      setReportLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Identity Verification — {personLabel}</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          The client starts and completes this themselves from their secure portal link.
        </p>
      </div>

      {checksError && <Alert type="error" className="mb-3">{checksError}</Alert>}

      {pointsSummary && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm font-medium text-gray-900">
            {pointsSummary.totalPoints} / {pointsSummary.required} points
          </span>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${pointsSummary.met ? identityCheckStatusClasses('verified') : identityCheckStatusClasses('pending')}`}>
            {pointsSummary.met ? '100-point check met' : 'Not yet met'}
          </span>
        </div>
      )}

      <details className="mb-3 border border-gray-100 rounded-lg">
        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-gray-700">
          Points table
        </summary>
        <div className="px-3 pb-3 space-y-2">
          {IDENTITY_DOC_TIERS.map((tier) => (
            <div key={tier.title}>
              <p className="text-xs font-medium text-gray-800">
                {tier.title} — {tier.points} pts each <span className="font-normal text-gray-400">({tier.note})</span>
              </p>
              <p className="text-xs text-gray-500">{tier.types.map((t) => DOC_TYPE_LABELS[t]).join(', ')}</p>
            </div>
          ))}
        </div>
      </details>

      {checksLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : identityChecks.length === 0 ? (
        <p className="text-sm text-gray-500">No ID documents submitted yet</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {identityChecks.map((check) => (
            <li key={check.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                {check.method === 'didit' ? (
                  <span className="text-sm font-medium text-gray-900">
                    Didit KYC verification
                    {check.status === 'verified' && (
                      <span className="text-xs font-normal text-gray-500"> · {check.points} pts</span>
                    )}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleViewCheckDocument(check)}
                    className="text-sm font-medium text-gray-900 hover:text-primary hover:underline"
                  >
                    {DOC_TYPE_LABELS[check.docType]}
                    {check.status === 'verified' && (
                      <span className="text-xs font-normal text-gray-500"> · {check.points} pts</span>
                    )}
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-0.5">
                  {check.method === 'didit' ? 'Started' : 'Submitted'} {formatDate(check.createdAt)}
                  {check.reviewedAt && ` · Reviewed by ${check.reviewedBy} on ${formatDate(check.reviewedAt)}`}
                  {check.notes && ` · ${check.notes}`}
                  {check.reportSummary?.fullName && ` · ${check.reportSummary.fullName}`}
                </p>
                {check.method === 'didit' && check.status === 'pending' && check.verificationUrl && (
                  <a
                    href={check.verificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline mt-0.5 inline-block"
                  >
                    Open verification link →
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${identityCheckStatusClasses(check.status)}`}>
                  {check.status}
                </span>
                {check.method === 'didit' && (
                  <Button type="button" size="sm" variant="secondary" onClick={() => handleViewReport(check)}>
                    View Report
                  </Button>
                )}
                {check.docType === 'medicare-card' && check.medicareValidation && (
                  <Button type="button" size="sm" variant="secondary" onClick={() => setMedicareModalCheck(check)}>
                    View Verification
                  </Button>
                )}
                {check.status !== 'verified' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => handleReviewCheck(check, 'verified')}
                    disabled={reviewingId === check.id}
                  >
                    Verify
                  </Button>
                )}
                {check.status !== 'rejected' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleReviewCheck(check, 'rejected')}
                    disabled={reviewingId === check.id}
                  >
                    Reject
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!reportModalCheck} onClose={() => setReportModalCheck(null)} title="Didit Verification Report" size="lg">
        {reportError && <Alert type="error" className="mb-3">{reportError}</Alert>}
        {reportLoading ? (
          <p className="text-sm text-gray-500">Loading report…</p>
        ) : report ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="sm:col-span-2 grid grid-cols-2 gap-x-4 gap-y-3">
              {REPORT_FIELDS.map(({ key, label }) => {
                const value = report[key]
                if (!value) return null
                return (
                  <div key={key}>
                    <p className="text-xs text-gray-500">{label}</p>
                    <p className="text-sm text-gray-900">{String(value)}</p>
                  </div>
                )
              })}
              {report.warnings.length > 0 && (
                <div className="col-span-2 mt-2">
                  <p className="text-xs text-gray-500 mb-1">Warnings</p>
                  <ul className="space-y-1">
                    {report.warnings.map((w, i) => (
                      <li key={i} className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                        {w.shortDescription || w.risk || 'Warning'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="space-y-3">
              {[
                { url: report.portraitImageUrl, label: 'Portrait' },
                { url: report.frontImageUrl, label: 'Document front' },
                { url: report.backImageUrl, label: 'Document back' },
              ].filter((img) => img.url).map((img) => (
                <a key={img.label} href={img.url!} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={img.url!} alt={img.label} className="w-full rounded-lg border border-gray-200 object-cover" />
                  <p className="text-xs text-gray-500 mt-1">{img.label} — open full size</p>
                </a>
              ))}
            </div>
          </div>
        ) : !reportError ? (
          <p className="text-sm text-gray-500">No report available</p>
        ) : null}
      </Modal>

      <Modal open={!!medicareModalCheck} onClose={() => setMedicareModalCheck(null)} title="Medicare Verification">
        {medicareModalCheck?.medicareValidation && (
          <div className="space-y-4">
            {medicareModalCheck.medicareValidation.error ? (
              <Alert type="error">{medicareModalCheck.medicareValidation.error}</Alert>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Match result</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${identityCheckStatusClasses(medicareModalCheck.status)}`}>
                  {medicareModalCheck.medicareValidation.matchType?.replace(/_/g, ' ') ?? 'Unknown'}
                </span>
              </div>
            )}

            {medicareModalCheck.medicareValidation.fieldValidations && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm text-gray-900 capitalize">{medicareModalCheck.medicareValidation.fieldValidations.fullName?.replace(/_/g, ' ') ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Date of birth</p>
                  <p className="text-sm text-gray-900 capitalize">{medicareModalCheck.medicareValidation.fieldValidations.dateOfBirth?.replace(/_/g, ' ') ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Card number</p>
                  <p className="text-sm text-gray-900 capitalize">{medicareModalCheck.medicareValidation.fieldValidations.identificationNumber?.replace(/_/g, ' ') ?? '—'}</p>
                </div>
              </div>
            )}

            {medicareModalCheck.medicareValidation.submitted && (
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Submitted for this check</p>
                <p className="text-sm text-gray-900">
                  {medicareModalCheck.medicareValidation.submitted.firstName} {medicareModalCheck.medicareValidation.submitted.lastName}
                  {' · DOB '}{formatDate(medicareModalCheck.medicareValidation.submitted.dateOfBirth)}
                  {' · Card ending '}{medicareModalCheck.medicareValidation.submitted.medicareCardNumber.slice(-4)}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
